import React, { useEffect } from 'react';
import { Col, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { LabelInput, LabelRequiredInput, LabelError } from 'shared/components/Labels';
import { TextField } from 'shared/components/form/Inputs';
import DropdownSearch from 'shared/components/DropdownSearch';
import { Button } from 'shared/components/Buttons';

import FieldType from 'shared/helpers/fieldType';
import { cleanSpaceString, emptyString } from 'shared/utils';
import { CustomFieldProps } from 'shared/prop-types/ReducerProps';
import { colors } from 'shared/defaultTheme';

const TextAreaForm = ({ defaultValue, onCancel, onSubmit }) => {
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const customField = useSelector((state) => state.customField);
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: defaultValue
      ? {
          fieldTitle: defaultValue.name,
          maxCharAllowed: defaultValue.size,
          groupId: defaultValue.parentId,
        }
      : undefined,
  });

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  useEffect(() => {
    if (defaultValue) {
      setValue('fieldTitle', getNameSelectedLanguage(defaultValue.i18List));
    }
  }, [defaultValue]);

  return (
    <Row className="row-content w-100">
      <Col sm={12}>
        <form className="form form-field" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Row className="w-100">
            <Col sm={12}>
              <div className="form__form-group">
                <LabelRequiredInput
                  className="form__form-group-label"
                  label={t('custom_field_form_title_label')}
                  color={colors.black}
                  error={errors.fieldTitle !== undefined}
                />
                <Controller
                  name="fieldTitle"
                  control={control}
                  defaultValue=""
                  rules={{ required: { value: true, message: t('general_required_field') } }}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={(e) => {
                        const newEvent = cleanSpaceString(e);
                        field.onChange(newEvent);
                      }}
                      onBlur={field.onBlur}
                      error={errors.fieldTitle !== undefined}
                    />
                  )}
                />
                {errors.fieldTitle && <LabelError label={errors.fieldTitle.message} />}
              </div>
            </Col>
          </Row>
          <Row className="w-100">
            <Col sm={12}>
              <div className="form__form-group">
                <LabelInput
                  className="form__form-group-label"
                  label={t('custom_field_max_characters')}
                  color={colors.black}
                  error={errors.maxCharAllowed !== undefined}
                />
                <Controller
                  name="maxCharAllowed"
                  control={control}
                  defaultValue=""
                  rules={{
                    max: { value: 300, message: t('custom_field_three_hundred_allowed') },
                  }}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      placeholder={t('general_default_value')}
                      value={!emptyString(field.value) ? parseInt(field.value, 10) : field.value}
                      step="1"
                      pattern="[0-9]{4}"
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.maxCharAllowed !== undefined}
                    />
                  )}
                />
                {errors.maxCharAllowed ? (
                  <LabelError label={errors.maxCharAllowed.message} />
                ) : (
                  <LabelError
                    className="helper-text"
                    label={t('custom_field_three_hundred_allowed')}
                  />
                )}
              </div>
            </Col>
          </Row>
          <Row className="w-100">
            <Col sm={12}>
              <div className="form__form-group">
                <LabelInput
                  className="form__form-group-label"
                  label={t('custom_field_choose_group')}
                  color={colors.black}
                  error={errors.groupId !== undefined}
                />
                <Controller
                  name="groupId"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <DropdownSearch
                      id="text-area-form-dropdown-search"
                      options={customField.customFields
                        .filter((item) => item.type === FieldType.GROUP)
                        .map((item) => ({
                          name: getNameSelectedLanguage(item.i18List),
                          value: item.id,
                        }))}
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      error={errors.groupId !== undefined}
                    />
                  )}
                />
                {errors.groupId && <LabelError label={errors.groupId.message} />}
              </div>
            </Col>
          </Row>
          <Row className="w-100">
            <Col className="d-flex flex-row justify-content-end" sm={12}>
              <Button
                className="text-uppercase mr-4"
                type="button"
                color="secondary"
                label={t('general_button_cancel')}
                onClick={onCancel}
              />
              <Button
                className="text-uppercase"
                type="submit"
                color="primary"
                label={t(defaultValue ? 'general_button_update' : 'general_add')}
              />
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  );
};
TextAreaForm.defaultProps = {
  defaultValue: undefined,
};

TextAreaForm.propTypes = {
  defaultValue: CustomFieldProps,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default TextAreaForm;
